import React from 'react';
import {
    Button as AriaButton,
    Disclosure as AriaDisclosure,
    type DisclosureProps as AriaDisclosureProps,
    DisclosurePanel,
    Heading,
    composeRenderProps,
} from 'react-aria-components';

import { classnames } from '@/utils/classnames.ts';
import mergeRefs from '@/utils/mergeRefs.ts';
import usePrettyWrap from '@/utils/usePrettyWrap.ts';

import { DateColumnProvider } from './DateColumn.tsx';
import { DisclosureChevron } from './Disclosure.tsx';
import Slot, { type Slottable } from './Slot.tsx';

// Children are separated by a middle dot, e.g. "hetzner.com · Vendor · DE123456789".
// The dot sits in the margin before an item rather than in it, so it's never part of a link: not underlined, not pressed.
// Every item has one, centred on it, so it stays on the text's line beside an item that's taller, e.g. of buttons; the
// first of each line has it in the margin the container moves left and clips off, so a wrapped line doesn't start with
// a dot.
const dotSeparatedClassName =
    '-ml-5 flex flex-wrap items-center gap-y-1 [clip-path:inset(-1rem_-1rem_-1rem_1.25rem)] *:relative *:ml-5 *:before:pointer-events-none *:before:absolute *:before:top-1/2 *:before:-left-2.5 *:before:-translate-x-1/2 *:before:-translate-y-1/2 *:before:text-text-subtle *:before:content-["·"]';

// Its rows' dates in one column across all of its lists (DateColumnProvider).
const PageRoot = ({ className, ...props }: React.ComponentPropsWithRef<'div'>) => (
    <DateColumnProvider>
        <div {...props} className={classnames('mx-auto w-full max-w-250 px-4 py-10 md:px-8 md:py-14', className)} />
    </DateColumnProvider>
);

// A grid, so the actions stay on the title's line and the subtitle runs below both: the eyebrow above the title, the
// media (an icon or avatar) left of it, the subtitle under it to the right edge. On phones the media goes above and the
// actions below, so the title has the whole width rather than being cut off after a few letters. Rows without content
// collapse, since the parts space themselves with margins.
const PageHeader = ({ className, ...props }: React.ComponentPropsWithRef<'header'>) => (
    <header
        {...props}
        // A toolbar filters what's below, so it sits closer to the header than the content does. Tabs split the whole
        // page below, so the header gets more room around it.
        className={classnames(
            "mb-10 grid grid-cols-[auto_minmax(0,1fr)_auto] items-center [grid-template-areas:'._eyebrow_eyebrow'_'media_title_actions'_'._subtitle_subtitle'] max-sm:[grid-template-areas:'media_media_media'_'eyebrow_eyebrow_eyebrow'_'title_title_title'_'subtitle_subtitle_subtitle'_'actions_actions_actions'] [&:has(+[data-page-toolbar])]:mb-6 [&:has(+[data-page-tabs])]:mt-4",
            className
        )}
    />
);

// An icon or avatar left of the title, above it on phones.
const PageMedia = ({ className, ...props }: React.ComponentPropsWithRef<'div'>) => (
    <div
        {...props}
        className={classnames(
            'mr-4 flex [grid-area:media] max-sm:mr-0 max-sm:mb-4 max-sm:justify-self-start',
            className
        )}
    />
);

// Groups the title with its eyebrow and subtitle; they're placed in the header's grid themselves.
const PageHeading = ({ className, ...props }: React.ComponentPropsWithRef<'div'>) => (
    <div {...props} className={classnames('contents', className)} />
);

// Above the title, e.g. the status of what the page shows.
export interface PageEyebrowProps extends React.ComponentPropsWithRef<'div'> {
    // `lg` above a large title (`size="lg"`): a line of gray text, e.g. today's date.
    size?: 'md' | 'lg';
}

const PageEyebrow = ({ size = 'md', className, ...props }: PageEyebrowProps) => (
    <div
        {...props}
        className={classnames(
            'flex flex-wrap items-center gap-2 [grid-area:eyebrow]',
            size === 'md' ? 'mb-3' : 'mb-5 font-medium text-eyebrow',
            className
        )}
    />
);

export interface PageTitleProps extends React.ComponentPropsWithRef<'h1'> {
    // `lg` for a page that's mostly its heading, e.g. the Übersicht's greeting or the error page.
    size?: 'md' | 'lg';
    // After a title given as text, e.g. a star: kept with its last word, so it doesn't wrap onto a line of its own.
    after?: React.ReactNode;
}

const PageTitle = ({ size = 'md', after, className, ref, children, ...props }: PageTitleProps) => {
    const titleRef = usePrettyWrap<HTMLHeadingElement>();

    // Kept from wrapping together with what follows the title's last space or hyphen, so "Website-Relaunch" still breaks
    // at its hyphen.
    if (after && typeof children === 'string') {
        const splitAt = Math.max(children.lastIndexOf(' '), children.lastIndexOf('-')) + 1;
        children = (
            <span>
                {children.slice(0, splitAt)}
                <span className="whitespace-nowrap">
                    {children.slice(splitAt)}
                    <span className="ml-3 inline-flex align-middle">{after}</span>
                </span>
            </span>
        );
    }

    return (
        <h1
            {...props}
            ref={mergeRefs(titleRef, ref)}
            className={classnames(
                'flex flex-wrap items-center gap-x-3 gap-y-1 font-headline font-semibold tracking-tight text-pretty [grid-area:title] max-sm:hyphens-auto [&>span]:min-w-0 [&>span]:overflow-hidden [&>span]:text-ellipsis max-sm:[&>span]:overflow-visible max-sm:[&>span]:wrap-break-word',
                size === 'md' ? 'text-4xl' : 'text-3xl leading-tight md:text-5xl md:leading-tight',
                className
            )}
        >
            {/* Bare text can't be styled, so it gets a span; a single word wider than the page, like a file name, is
                then cut off with an ellipsis instead of running past the edge. On phones, where a long German word
                like „Teilnehmerbegrenzung“ is that wide already, it's hyphenated, and what can't be is broken rather
                cut off: Safari cut a hyphenated line that came out a little too wide, leaving an ellipsis after the
                hyphen. */}
            {typeof children === 'string' ? <span>{children}</span> : children}
        </h1>
    );
};

// A sentence under a large title (`size="lg"`) in place of the subtitle's line of details.
const PageLead = ({ className, ref, ...props }: React.ComponentPropsWithRef<'p'>) => {
    const leadRef = usePrettyWrap<HTMLParagraphElement>();

    return (
        <p
            {...props}
            ref={mergeRefs(leadRef, ref)}
            className={classnames('mt-6 max-w-2xl text-lg text-pretty text-text-muted [grid-area:subtitle]', className)}
        />
    );
};

const PageSubtitle = ({ className, ...props }: React.ComponentPropsWithRef<'div'>) => (
    <div
        {...props}
        className={classnames(dotSeparatedClassName, 'mt-3 text-text-muted [grid-area:subtitle]', className)}
    />
);

const PageActions = ({ className, ...props }: React.ComponentPropsWithRef<'div'>) => (
    <div
        {...props}
        className={classnames(
            'ml-4 flex shrink-0 items-center gap-2 [grid-area:actions] max-sm:mt-5 max-sm:ml-0 max-sm:flex-wrap',
            className
        )}
    />
);

// Search and filters for the page's content, right below the header.
const PageToolbar = ({ className, ...props }: React.ComponentPropsWithRef<'div'>) => (
    <div {...props} data-page-toolbar className={classnames('mb-6 flex flex-wrap items-center gap-2', className)} />
);

// Right below a toolbar it starts at the toolbar's own margin, as a list without sections does.
const PageSection = ({ className, ...props }: React.ComponentPropsWithRef<'section'>) => (
    <section
        {...props}
        className={classnames('mt-12 scroll-mt-8 first:mt-0 [[data-page-toolbar]+&]:mt-0', className)}
    />
);

// Padded rather than of a fixed height, so a hint under the title keeps the same room above the line as a title alone.
// What's after the title, e.g. a small button, reaches into that padding, so a header is as high with it as without
// and nothing below moves while it comes and goes. Where it doesn't fit beside the title and its hint, e.g. on phones, it
// goes onto the next line rather than squeezing the hint into a narrow column. On phones it then starts where the title
// does, a ghost button by its text: the title fills its line, so what still fits beside it stays at the end.
const PageSectionHeader = ({ className, ...props }: React.ComponentPropsWithRef<'header'>) => (
    <header
        {...props}
        className={classnames(
            'flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-border py-2.5 *:not-first:-my-1',
            // A long hint wraps beside the actions, rather than pushing them onto a line of their own.
            'sm:*:first:min-w-0 sm:*:first:flex-1',
            'max-sm:justify-start max-sm:*:first:grow max-sm:*:not-first:ml-0 max-sm:[&>:not(:first-child)_[data-variant=ghost]:first-child]:-ml-3.5 max-sm:[&>[data-variant=ghost]:nth-child(2)]:-ml-3.5',
            className
        )}
    />
);

const PageSectionTitle = ({ className, ...props }: React.ComponentPropsWithRef<'h2'>) => (
    <h2 {...props} className={classnames('font-headline text-xl font-medium select-none', className)} />
);

export interface PageCollapsibleSectionProps extends Omit<AriaDisclosureProps, 'children'> {
    title: React.ReactNode;
    // At the end of the header, open or closed, e.g. a link.
    action?: React.ReactNode;
    children: React.ReactNode;
}

interface PageListSectionProps {
    // May hold a Badge after the text, e.g. how many are in it.
    title: React.ReactNode;
    // At the end of the header, e.g. a button.
    action?: React.ReactNode;
    // Its rows, divided by hairlines.
    children: React.ReactNode;
}

// A group of a list page's rows under its title, e.g. a day or what waits. Where the title and the action don't fit on
// one line, the action goes onto the next rather than the title breaking.
const PageListSection = ({ title, action, children }: PageListSectionProps) => (
    <PageSection className="mt-10">
        <PageSectionHeader>
            <PageSectionTitle className="flex items-center gap-2.5">{title}</PageSectionTitle>
            {action && <div className="ml-auto">{action}</div>}
        </PageSectionHeader>
        <div className="divide-rows">{children}</div>
    </PageSection>
);

// A section whose title opens and closes it; open unless `defaultExpanded` says otherwise.
const PageCollapsibleSection = ({
    title,
    action,
    defaultExpanded = true,
    className,
    children,
    ...props
}: PageCollapsibleSectionProps) => {
    return (
        <AriaDisclosure
            {...props}
            defaultExpanded={defaultExpanded}
            className={composeRenderProps(className, (className) => classnames('mt-12 first:mt-0', className))}
        >
            <PageSectionHeader>
                <Heading className="flex min-w-0 text-xl font-medium">
                    <AriaButton
                        slot="trigger"
                        className="-my-0.5 -ml-2 flex min-w-0 items-center gap-1.5 rounded-lg py-0.5 pr-2 pl-1 outline-none data-[focus-visible]:ring-2 data-[focus-visible]:ring-primary data-[hovered]:bg-surface-muted"
                    >
                        <DisclosureChevron className="size-5 text-text-muted" />
                        <span className="truncate">{title}</span>
                    </AriaButton>
                </Heading>
                {action}
            </PageSectionHeader>
            <DisclosurePanel>{children}</DisclosurePanel>
        </AriaDisclosure>
    );
};

// A group's heading within a section or a dialog, e.g. a day in a list or a kind of item. Use asChild for the same look
// on a label that isn't a heading, e.g. a decision's topic above it.
const PageGroupTitle = ({ asChild, className, ...props }: React.ComponentPropsWithRef<'h3'> & Slottable) => {
    const Comp = asChild ? Slot : 'h3';
    return <Comp {...props} className={classnames('text-sm font-medium text-text-muted', className)} />;
};

// A short explanation under a section's title, inside the section header.
const PageSectionHint = ({ className, ...props }: React.ComponentPropsWithRef<'p'>) => (
    <p {...props} className={classnames('text-sm text-text-muted', className)} />
);

// Said in place of a list that has nothing in it.
const PageEmpty = ({ className, ...props }: React.ComponentPropsWithRef<'p'>) => (
    <p {...props} className={classnames('py-4 text-text-muted', className)} />
);

export default Object.assign(PageRoot, {
    Header: PageHeader,
    Media: PageMedia,
    Eyebrow: PageEyebrow,
    Heading: PageHeading,
    Title: PageTitle,
    Subtitle: PageSubtitle,
    Lead: PageLead,
    Actions: PageActions,
    Toolbar: PageToolbar,
    Section: PageSection,
    CollapsibleSection: PageCollapsibleSection,
    ListSection: PageListSection,
    SectionHeader: PageSectionHeader,
    SectionTitle: PageSectionTitle,
    SectionHint: PageSectionHint,
    GroupTitle: PageGroupTitle,
    Empty: PageEmpty,
});
